<script setup lang="ts">
/**
 * Presentational list of treasury accounts.
 *
 * Receives rows + total via props; emits update:page / action events.
 * The container owns the mutation lifecycle and the form / confirm
 * dialogs.
 *
 * Row actions are computed per row — "Set as default" hidden when the
 * row is already default or inactive; "Deactivate" hidden when the row
 * is inactive.
 *
 * Title / subtitle are intentionally NOT passed to DataTableV2 — the
 * container's page header is the single source of truth for the
 * "Treasury accounts / N accounts" header line. Passing them here
 * caused a visible duplicate on this page; do not re-add.
 */
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'
import Badge from '@uiKit/components/Badge.vue'
import DataTableV2, { type TableColumn, type TableAction } from '@uiKit/components/DataTableV2.vue'
import {
  TREASURY_BANK_ACCOUNT_STATUS,
  type TreasuryBankAccount,
} from '../types/treasuryAccountsTypes'
import {
  getTreasuryAccountStatusColor,
  getTreasuryAccountStatusLabel,
} from '../utils/treasuryAccountsFormatters'

defineProps<{
  rows: TreasuryBankAccount[]
  loading: boolean
  total: number
  page?: number
  pageSize?: number
}>()

const emit = defineEmits<{
  (e: 'update:page', value: number): void
  (e: 'update:pageSize', value: number): void
  (e: 'view-transactions', row: TreasuryBankAccount): void
  (e: 'edit', row: TreasuryBankAccount): void
  (e: 'set-default', row: TreasuryBankAccount): void
  (e: 'deactivate', row: TreasuryBankAccount): void
}>()

const columns: TableColumn[] = [
  { key: 'accountName', label: 'ACCOUNT NAME', width: 'minmax(200px, 1.4fr)' },
  { key: 'bankName', label: 'BANK', width: 'minmax(160px, 1fr)' },
  { key: 'currencyCode', label: 'CURRENCY', width: 'minmax(100px, 0.6fr)' },
  { key: 'countryCode', label: 'COUNTRY', width: 'minmax(100px, 0.6fr)' },
  { key: 'status', label: 'STATUS', width: 'minmax(140px, 1fr)' },
  { key: 'isDefault', label: 'DEFAULT', width: 'minmax(120px, 0.8fr)' },
]

const actions: TableAction<TreasuryBankAccount>[] = [
  {
    label: 'View transactions',
    icon: 'arrow-right',
    onClick: (row) => emit('view-transactions', row),
  },
  {
    label: 'Edit',
    icon: 'pencil',
    onClick: (row) => emit('edit', row),
  },
  {
    label: 'Set as default',
    icon: 'star',
    hidden: (row) => row.isDefault || row.status !== TREASURY_BANK_ACCOUNT_STATUS.ACTIVE,
    onClick: (row) => emit('set-default', row),
  },
  {
    label: 'Deactivate',
    icon: 'ban',
    danger: true,
    hidden: (row) => row.status !== TREASURY_BANK_ACCOUNT_STATUS.ACTIVE,
    onClick: (row) => emit('deactivate', row),
  },
]
</script>

<template>
  <DataTableV2
    :columns="columns"
    :rows="rows"
    :loading="loading"
    row-key="id"
    :searchable="false"
    :local-search="false"
    :local-pagination="true"
    :page="page"
    :page-size="pageSize"
    :url-sync="true"
    page-size-storage-key="treasury.accounts.pageSize"
    :actions="actions"
    empty-title="No treasury accounts"
    empty-subtitle="Add a treasury account to start receiving deposits and processing withdrawals."
    @update:page="emit('update:page', $event)"
    @update:page-size="emit('update:pageSize', $event)"
  >
    <template #cell-accountName="{ row }">
      <Text type="body" weight="semibold">{{ (row as TreasuryBankAccount).accountName }}</Text>
    </template>

    <template #cell-bankName="{ row }">
      <Text type="body-sm">{{ (row as TreasuryBankAccount).bankName }}</Text>
    </template>

    <template #cell-currencyCode="{ row }">
      <Text type="body-sm" color="muted">{{ (row as TreasuryBankAccount).currencyCode }}</Text>
    </template>

    <template #cell-countryCode="{ row }">
      <Text type="body-sm" color="muted">{{ (row as TreasuryBankAccount).countryCode }}</Text>
    </template>

    <template #cell-status="{ row }">
      <StatusBadge
        :color="getTreasuryAccountStatusColor((row as TreasuryBankAccount).status)"
        :label="getTreasuryAccountStatusLabel((row as TreasuryBankAccount).status)"
      />
    </template>

    <template #cell-isDefault="{ row }">
      <Box flex>
        <Badge v-if="(row as TreasuryBankAccount).isDefault" label="Default" color="green" />
        <Text v-else type="body-sm" color="muted">—</Text>
      </Box>
    </template>
  </DataTableV2>
</template>
